import { Sparkles } from "lucide-react";
import { LoadingSkeleton } from "@strivelabs/ui-blocks";
import { cn } from "@/lib/utils";

interface ReportCanvasSplashProps {
  title?: string;
  description?: string;
  className?: string;
}

/**
 * Splash shown while the report canvas is being prepared — typically while
 * the backend syncs the report into the tenant sandbox and we fetch the HTML
 * body. Pairs a soft brand-tinted message with a code-shaped skeleton so
 * the transition into the rendered canvas reads as the same surface
 * filling in, rather than two separate screens.
 */
export function ReportCanvasSplash({
  title = "Preparing your report…",
  description = "We're getting your report ready to edit. Hang tight — this only takes a moment.",
  className,
}: ReportCanvasSplashProps) {
  return (
    <div className={cn("relative h-full w-full overflow-hidden", className)}>
      <LoadingSkeleton variant="code" />

      {/* Hero message overlaid on top of the skeleton. The skeleton's
          `animate-pulse` keeps the surface alive; this layer adds intent. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center pointer-events-none">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary-100 text-primary shadow-sm">
          <Sparkles className="size-6" />
        </div>
        <div className="space-y-1 max-w-md">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}
